import { Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TIME_BUCKETS, TIME_SINKS, DELIVERY_Q, OTHER_TEXT, WORKED_TEXT } from '@/lib/editorTime';

/**
 * The two questions an editor answers with each delivery, and the optional line about what worked for the
 * client (lib/editorTime.js, backend app/editor_time.py). Controlled: the deliver panel that renders it owns
 * the answers and sends them with its own deliver request, which the server refuses without them. The video
 * upload, the culling delivery and both photo paths all render this one block.
 */
export function DeliveryQuestions({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const label = 'font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500';
  return (
    <div className="flex flex-col gap-3 p-4 rounded-lg border bg-ug-surface/50 border-gold-leaf/20" data-testid="delivery-questions">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-ug-gold" />
        <h3 className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold/80">About this delivery</h3>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={label}>{DELIVERY_Q.time} *</span>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={DELIVERY_Q.time}>
          {TIME_BUCKETS.map(([k, text]) => {
            const on = value.time_bucket === k;
            return (
              <button key={k} type="button" role="radio" aria-checked={on} onClick={() => set('time_bucket', k)}
                data-testid={`delivery-time-${k}`} data-active={on ? 'true' : 'false'}
                className={`tap-auto px-3 py-1.5 rounded border font-sans text-[11px] font-medium transition-all ${
                  on ? 'border-ug-gold/40 bg-ug-gold/[0.06] text-ug-gold' : 'border-ug-border text-slate-400 hover:border-ug-border-strong'}`}>
                {text}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={label}>{DELIVERY_Q.sink} *</span>
        <Select value={value.time_sink} onValueChange={v => set('time_sink', v)}>
          <SelectTrigger data-testid="delivery-sink" className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-xs">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent className="bg-ug-raised border-ug-border-strong">
            {TIME_SINKS.map(([k, text]) => <SelectItem key={k} value={k} className="text-zinc-100 text-xs">{text}</SelectItem>)}
          </SelectContent>
        </Select>
        {value.time_sink === 'other' && (
          <Input value={value.time_sink_other} onChange={e => set('time_sink_other', e.target.value)} maxLength={OTHER_TEXT}
            placeholder="What was it?" data-testid="delivery-sink-other"
            className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-[11px]" />
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={label}>{DELIVERY_Q.worked} <span className="normal-case tracking-normal font-normal text-slate-600">(optional)</span></span>
        <textarea value={value.what_worked} onChange={e => set('what_worked', e.target.value)} rows={2} maxLength={WORKED_TEXT}
          placeholder="One line the next editor on this client should know" data-testid="delivery-worked"
          className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-ug-gold/40 outline-none resize-y" />
      </div>
    </div>
  );
}
